import React, { useRef, useState, useEffect } from 'react';
import { Popover, Input, Button, Switch, Divider, message, FloatButton } from 'antd';
import { BugOutlined } from '@ant-design/icons';

const DevelopmentOptions = ({ onSimulateScan }) => {
  const [isDevIdleDisabled, setIsDevIdleDisabled] = useState(() => localStorage.getItem('dev_idle_disabled') === 'true');
  const [isVisible, setIsVisible] = useState(() => localStorage.getItem('dev_options_visible') === 'true');

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        setIsVisible(prev => {
          const next = !prev;
          localStorage.setItem('dev_options_visible', next);
          return next;
        });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  
  // Dragging state for Dev Options Button
  const [devBtnPos, setDevBtnPos] = useState(() => {
    const saved = localStorage.getItem('dev_options_pos');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {}
    }
    return { left: 40, bottom: 40 };
  });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef({ dragging: false, startX: 0, startY: 0, initLeft: 40, initBottom: 40, hasMoved: false, lastPos: null });

  const onPointerDown = (e) => {
    dragRef.current.dragging = true;
    dragRef.current.hasMoved = false;
    dragRef.current.startX = e.clientX;
    dragRef.current.startY = e.clientY;
    dragRef.current.initLeft = devBtnPos.left;
    dragRef.current.initBottom = devBtnPos.bottom;
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!dragRef.current.dragging) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
       dragRef.current.hasMoved = true;
       setIsDragging(true);
    }
    const newPos = {
      left: dragRef.current.initLeft + dx,
      bottom: dragRef.current.initBottom - dy, // y decreases downwards, bottom increases upwards
    };
    dragRef.current.lastPos = newPos;
    setDevBtnPos(newPos);
  };

  const onPointerUp = (e) => {
    dragRef.current.dragging = false;
    setTimeout(() => setIsDragging(false), 50);
    e.currentTarget.releasePointerCapture(e.pointerId);
    if (dragRef.current.hasMoved && dragRef.current.lastPos) {
      localStorage.setItem('dev_options_pos', JSON.stringify(dragRef.current.lastPos));
    }
  };

  const handleScanSubmit = () => {
    const val = document.getElementById('test-scan-input')?.value;
    if (val && onSimulateScan) onSimulateScan(val);
  };

  if (!isVisible) return null;

  return (
    <Popover 
      content={
        <div style={{ width: '260px', padding: '4px 0' }}>
          {/* Simulate Scan Section */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#9CA3AF', marginBottom: '8px', letterSpacing: '0.05em' }}>
              SIMULATE SCAN
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Input 
                id="test-scan-input" 
                placeholder="Enter barcode..." 
                style={{ flex: 1 }}
                onPressEnter={handleScanSubmit}
              />
              <Button type="primary" onClick={handleScanSubmit}>
                Scan
              </Button>
            </div>
          </div>
          
          <Divider style={{ margin: '12px 0' }} />
          
          {/* Kiosk Behavior Section */}
          <div>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#9CA3AF', marginBottom: '12px', letterSpacing: '0.05em' }}>
              KIOSK BEHAVIOR
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>Disable Idle Timeout</span>
              <Switch 
                checked={isDevIdleDisabled} 
                onChange={(checked) => {
                  setIsDevIdleDisabled(checked);
                  localStorage.setItem('dev_idle_disabled', checked);
                  message.success(`Idle timeout ${checked ? 'disabled' : 'enabled'}. Please refresh to apply.`);
                }}
              />
            </div>
          </div>
        </div>
      } 
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' }}>
          <BugOutlined style={{ color: '#007bff' }} />
          <span style={{ fontWeight: 600 }}>Development Options</span>
        </div>
      }
      trigger="click"
    >
      <div 
        style={{ 
          position: 'fixed', 
          left: devBtnPos.left, 
          bottom: devBtnPos.bottom, 
          zIndex: 9999, 
          touchAction: 'none', 
          width: 40, 
          height: 40, 
          cursor: isDragging ? 'grabbing' : 'grab' 
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onClickCapture={(e) => { 
          if (dragRef.current.hasMoved) { 
            e.stopPropagation();
            e.preventDefault();
            dragRef.current.hasMoved = false; 
          } 
        }}
      >
        <FloatButton 
          icon={<BugOutlined />} 
          type="primary" 
          tooltip={!isDragging ? "Development Options" : ""}
          style={{ position: 'absolute', bottom: 0, left: 0, margin: 0, pointerEvents: 'none' }} 
        />
      </div>
    </Popover>
  );
};

export default DevelopmentOptions;
